import { Button, Card, Checkbox, Col, DatePicker, Dropdown, Form, Input, message, Menu, Modal, Radio, Row, Select, Space, Table, Tag, Tooltip } from 'antd';
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { hideAuthLoader, showAuthLoader } from '../../appRedux/actions/Auth';
import Service from '../../service';
import moment from 'moment';
import { DownloadOutlined, EyeOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import GuidedTour, { hasTourFlag } from "../common/GuidedTour/GuidedTour";
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { IoIosArrowDown } from 'react-icons/io';
import { getResource } from '../../util/getResourcePermission';
import config from "../../permission/config.json";
import { getRoles } from '../../util/getRoles';
import ApprovalStatusCell from '../Approvals/ApprovalStatusCell';
import useReportingManagerList from '../../hooks/useReportingManagerList';
import ColumnSelector from '../common/ColumnSelector';
import useColumnVisibility from '../../hooks/useColumnVisibility';
import EmpNameCodeCell from '../common/EmpNameCodeCell';
import useCompanyDateFormat from '../../hooks/useCompanyDateFormat';
import { TABLE_PAGINATION, DEFAULT_PAGE_SIZE } from '../../constants/tablePagination';

const { Option } = Select;

const TOUR_STORAGE_KEY = "tour_seen_bill_reimbursement_approval";
const TOUR_HINT_STORAGE_KEY = "tour_hint_dismissed_bill_reimbursement_approval";

const ClaimApprovalManagement = () => {
    const dateFormat = useCompanyDateFormat();
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const { authUser } = useSelector(({ auth }) => auth);
    const user_data = JSON.parse(localStorage.getItem('user_data') || '{}');

    // Pending Tasks links carry ?status= so this page opens already filtered
    // to what the count actually meant, same as every other approval screen
    // (Leave/WFH/Comp-off/etc.) - the value is role-correct as sent (RM/ARM
    // get "Pending", matching statusbyRM's own value; Admin/HR get
    // "allPending", matching the oversight filter's own enum). Absent for
    // any other navigation path, so this is a no-op otherwise.
    const initialStatusFilter = new URLSearchParams(window.location.search).get('status') || '';

    const [claimData, setClaimData] = useState([]);
    const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [applicationDate, setApplicationDate] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isTourOpen, setIsTourOpen] = useState(false);
    const [isTourHintOpen, setIsTourHintOpen] = useState(false);

    useEffect(() => {
      if (!hasTourFlag(TOUR_STORAGE_KEY) && !hasTourFlag(TOUR_HINT_STORAGE_KEY)) {
        setIsTourHintOpen(true);
      }
    }, []);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: DEFAULT_PAGE_SIZE,
    });
    const [totalRecords, setTotalRecords] = useState(0);
    const companySlug = localStorage.getItem("companyDomain");
    // Determine user role - RM and ARM share the RM-facing view/endpoint
    // (the backend's subordinate lookup already treats them the same),
    // and HR shares the Admin-facing oversight view.
    const isTrueRM = getRoles(["Reporting Manager"]);
    const isTrueARM = getRoles(["Assistance Reporting Manager"]);
    const isRM = isTrueRM || isTrueARM;
    // Sub-Admin gets the Admin-facing company-wide view, but acts as the
    // employee's RM: team rows only (server canAct flag), RM-stage rule below.
    const isSubAdmin = getRoles(["Sub-Admin"]);
    const isAdmin = getRoles(["Admin", "HR"]) || isSubAdmin;
    // The stage role this specific viewer holds in an engine-managed chain -
    // used to show their own level's decision, not just the overall status.
    const viewerApproverRole = isTrueRM ? "REPORTING_MANAGER" : isTrueARM ? "ASSISTANCE_REPORTING_MANAGER" : null;

    // Admin-only "Reporting Manager" filter - RM/ARM viewers are already scoped
    // to their own team by the list endpoint. Named apart from `isAdmin` above,
    // which on this screen means Admin *or* HR.
    const { isAdmin: isAdminRole, reportingManagerOptions } = useReportingManagerList();
    const [reportingManagerFilter, setReportingManagerFilter] = useState(null);

    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [filterForm] = Form.useForm(); // Add separate form for modal

    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const availableExportColumns = [
        { key: 'claimCode', label: 'Claim Code' },
        { key: 'applicationDate', label: 'Date' },
        { key: 'employeeCode', label: 'Employee Code' },
        { key: 'employeeName', label: 'Employee Name' },
        { key: 'totalAmount', label: 'Total Amount' },
        ...(isAdmin ? [{ key: 'statusbyAdmin', label: 'Admin Status' }] : []),
    ];
    const defaultExportColumns = availableExportColumns.map((col) => col.key);
    const [selectedExportColumns, setSelectedExportColumns] = useState(defaultExportColumns);

    const history = useHistory();

    // Fetch employees for dropdown
    const fetchEmployees = async () => {
        try {
            // let response;
            // if(isRM){

            // }else if(isAdmin){

            // }
            const response = await Service.makeAPICall({
                methodName: Service.postMethod,
                api_url: Service.empDropdownList,
            });

            if (response && response.data) {
                setEmployees(response.data.data || []);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const downloadReceipt = async (id) => {
        try {
            dispatch(showAuthLoader());
            const body = { id: id };
            const response = await Service.makeAPICall({
                methodName: Service.postMethod,
                api_url: Service.downloadReimbursementReceipt,
                body: body
            });
            if (response.data.pdfUrl) {
                window.open(response.data.pdfUrl, '_blank');
            }
            dispatch(hideAuthLoader());
        } catch (error) {
            message.error("Download failed");
            dispatch(hideAuthLoader());
        }
    };

    // Fetch claim records with filters
    const fetchClaimRecords = async () => {
        try {
            dispatch(showAuthLoader());

            // Build query string
            let query = `?page=${pagination.current}&limit=${pagination.pageSize}`;

            // Add filter parameter based on status
            if (statusFilter) {
                if (isRM) {
                    query += `&statusbyRM=${statusFilter}`;
                } else if (isAdmin) {
                    query += `&filter=${statusFilter}`;
                }
            }

            // Add employee filter
            if (selectedEmployee) {
                query += `&employee_id=${selectedEmployee}`;
            }

            // Add application date filter
            if (applicationDate) {
                query += `&applicationDate=${applicationDate.format('YYYY-MM-DD')}`;
            }

            // Add reporting manager filter
            if (reportingManagerFilter) {
                query += `&filter_reporting_manager_id=${reportingManagerFilter}`;
            }

            let response;
            if (isRM) {
                response = await Service.makeAPICall({
                    methodName: Service.getMethod,
                    api_url: Service.fetchRMClaims + query,
                });
            } else if (isAdmin) {
                response = await Service.makeAPICall({
                    methodName: Service.getMethod,
                    api_url: Service.fetchAdminClaims + query,
                });
            }

            if (response && response?.data) {
                setClaimData(response.data.data || []);
                setTotalRecords(response?.data?.totalData || 0);
            }

            dispatch(hideAuthLoader());
        } catch (error) {
            console.error(error);
            dispatch(hideAuthLoader());
            message.error("Failed to fetch reimbursements");
        }
    };

    // Bulk approve
    const handleBulkApprove = async () => {
        try {
            if (selectedRowKeys.length === 0) {
                return message.warning('Please select at least one claim');
            }

            dispatch(showAuthLoader());

            const payload = {
                claimIds: selectedRowKeys,
                status: 'Approved',
                approvedBy: authUser?._id,
            };

            const response = await Service.makeAPICall({
                methodName: Service.putMethod,
                api_url: Service.updateBulkClaims,
                body: JSON.stringify(payload),
            });

            // response.status is the raw HTTP status, which this API always
            // returns as 200 even on a rejected claim - the real outcome is
            // the app-level status/summary inside response.data.
            const summary = response?.data?.data?.summary;
            if (summary?.failed > 0) {
                message.warning(response?.data?.message || 'Some claims could not be approved');
            } else if (summary?.successful > 0) {
                message.success(response?.data?.message || 'Claims approved successfully');
            } else {
                message.error(response?.data?.message || 'Failed to approve claims');
            }
            setSelectedRowKeys([]);
            fetchClaimRecords();

            dispatch(hideAuthLoader());
        } catch (error) {
            console.error(error);
            dispatch(hideAuthLoader());
            message.error('Something went wrong');
        }
    };

    // Bulk reject
    const handleBulkReject = async () => {
        try {
            if (selectedRowKeys.length === 0) {
                return message.warning('Please select at least one claim');
            }

            dispatch(showAuthLoader());

            const payload = {
                claimIds: selectedRowKeys,
                status: 'Rejected',
                approvedBy: authUser?._id,
            };

            const response = await Service.makeAPICall({
                methodName: Service.putMethod,
                api_url: Service.updateBulkClaims,
                body: JSON.stringify(payload),
            });

            const summary = response?.data?.data?.summary;
            if (summary?.failed > 0) {
                message.warning(response?.data?.message || 'Some claims could not be rejected');
            } else if (summary?.successful > 0) {
                message.success(response?.data?.message || 'Claims rejected successfully');
            } else {
                message.error(response?.data?.message || 'Failed to reject claims');
            }
            setSelectedRowKeys([]);
            fetchClaimRecords();

            dispatch(hideAuthLoader());
        } catch (error) {
            console.error(error);
            dispatch(hideAuthLoader());
            message.error('Something went wrong');
        }
    };

    const handleClearFilters = () => {
        filterForm.resetFields();
        setApplicationDate(null);
        setSelectedEmployee(null);
        setStatusFilter('');
        setReportingManagerFilter(null);
        setPagination({ ...pagination, current: 1 });
    };

    const handleSearch = () => {
        // Reset to first page when searching
        setPagination({ ...pagination, current: 1 });
        fetchClaimRecords();
    };

    const getStatusTag = (status) => {
        const statusMap = {
            'Pending': { color: 'gold', text: 'Pending' },
            'Approved': { color: 'green', text: 'Approved' },
            'Rejected': { color: 'red', text: 'Rejected' },
            'Partially Approved': { color: 'orange', text: 'Partially Approved' },
        };

        const statusInfo = statusMap[status] || { color: 'default', text: status };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
    };

    // Engine-managed claims carry approval_progress - prefer the shared
    // status cell (click for the full per-level breakdown) over the plain
    // legacy tag whenever it's present; legacy claims fall back untouched.
    const renderStatus = (status, record) =>
        record?.approval_progress ? <ApprovalStatusCell progress={record.approval_progress} /> : getStatusTag(status);

    const LEVEL_STATUS_TAG = {
        PENDING: { color: 'orange', label: 'Pending' },
        APPROVED: { color: 'green', label: 'Approved' },
        REJECTED: { color: 'red', label: 'Rejected' },
        BYPASSED: { color: 'green', label: 'Approved' },
    };

    // For RM/ARM's own "Status" column: show THIS viewer's own level decision
    // (e.g. "Approved" the moment RM decides), not the overall chain status -
    // ApprovalStatusCell's default would show "Pending" until every level is
    // done, which reads as "you haven't decided yet" even after you have.
    const renderViewerStatus = (status, record) => {
        if (record?.approval_progress) {
            const myLevel = viewerApproverRole
                ? (record.approval_progress.levels || []).find((level) => level.role === viewerApproverRole)
                : null;
            const tag = myLevel ? LEVEL_STATUS_TAG[myLevel.status] : null;
            return (
                <ApprovalStatusCell
                    progress={record.approval_progress}
                    label={tag?.label}
                    color={tag?.color}
                />
            );
        }
        return getStatusTag(status);
    };

    const columns = [
        {
            title: 'Claim Code',
            dataIndex: 'claimCode',
            key: 'claimCode',
            width: 130,
            render: (text) => text || '-',
        },
        {
            title: 'Date',
            dataIndex: 'applicationDate',
            key: 'applicationDate',
            width: 130,
            render: (date) => date ? moment(date).format(dateFormat) : '-',
        },
        {
            title: 'Employee',
            dataIndex: 'employeeName',
            key: 'employeeName',
            width: 220,
            render: (text, record) => (
                <EmpNameCodeCell name={record?.creatorDetails?.full_name} code={record?.creatorDetails?.emp_code} />
            ),
        },
        {
            title: 'Reporting Manager',
            dataIndex: 'reportingManager',
            key: 'reportingManager',
            width: 180,
            render: (text, record) => record?.reportingManagerDetails?.full_name || '-',
        },
        {
            title: 'Total Amount',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            width: 140,
            render: (amount) => `₹${amount?.toFixed(2) || '0.00'}`,
        },
        ...(isAdmin ? [{
            title: 'Final Status',
            dataIndex: 'statusFinal',
            key: 'statusFinal',
            width: 130,
            render: renderStatus,
        }] : []),
        {
            // Admin/HR see this alongside Final Status/Admin Status, so it
            // stays labelled "RM Status" there; for RM/ARM it's their only
            // status column, so it's just "Status".
            title: isAdmin ? 'RM Status' : 'Status',
            dataIndex: 'statusbyRM',
            key: 'statusbyRM',
            width: 130,
            render: (status, record) => isAdmin ? getStatusTag(status) : renderViewerStatus(status, record),
        },
        ...(isAdmin ? [{
            title: 'Admin Status',
            dataIndex: 'statusbyAdmin',
            key: 'statusbyAdmin',
            width: 130,
            render: (status) => getStatusTag(status),
        }] : []),
        {
            title: 'Actions',
            key: 'actions',
            className: 'actions-column',
            width: 110,
            render: (text, record) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    {(record?.statusFinal === 'Approved' && isAdmin) ? (
                        <Button
                            type="link"
                            size="small"
                            onClick={() => downloadReceipt(record?._id)}
                        >
                            <DownloadOutlined style={{ fontSize: "18px" }} />
                        </Button>
                    ) : "No action available"}
                </div>
            ),
        }
    ];

    // Optional columns beyond the ones shown by default - every field here is
    // already returned by /claim/claim-rm and /claim/claim-admin (see
    // Claim.js getProjection), so no extra API calls are needed to offer them.
    const extraColumns = [
        {
            title: 'Project/Event Name',
            dataIndex: 'title',
            key: 'title',
            width: 180,
            render: (text) => text || '-',
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            width: 220,
            ellipsis: true,
            render: (text) => text || '-',
        },
        {
            title: 'Claim Type(s)',
            key: 'entries',
            width: 180,
            render: (text, record) => {
                const types = Array.from(new Set((record?.entries || []).map((e) => e.claimType).filter(Boolean)));
                return types.length ? types.join(', ') : '-';
            },
        },
        {
            title: 'Employee Email',
            key: 'creatorEmail',
            width: 220,
            ellipsis: true,
            render: (text, record) => record?.creatorDetails?.email || '-',
        },
        {
            title: 'Department',
            dataIndex: 'department',
            key: 'department',
            width: 160,
            render: (text, record) => record?.departmentDetails?.department_name || '-',
        },
        {
            title: 'RM Action Date',
            dataIndex: 'rmActionAt',
            key: 'rmActionAt',
            width: 150,
            render: (date) => (date ? moment(date).format(dateFormat) : '-'),
        },
        {
            title: 'RM Actioned By',
            key: 'rmActionByDetails',
            width: 160,
            render: (text, record) => record?.rmActionByDetails?.full_name || '-',
        },
        ...(isAdmin ? [
            {
                title: 'Admin Action Date',
                dataIndex: 'adminActionAt',
                key: 'adminActionAt',
                width: 160,
                render: (date) => (date ? moment(date).format(dateFormat) : '-'),
            },
            {
                title: 'Admin Actioned By',
                key: 'adminActionByDetails',
                width: 160,
                render: (text, record) => record?.adminActionByDetails?.full_name || '-',
            },
        ] : []),
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (date) => (date ? moment(date).format(dateFormat) : '-'),
        },
    ];

    // `columns` ends with the Actions column - slot the optional columns in
    // ahead of it so Actions stays pinned as the last column.
    const actionsColumnDef = columns[columns.length - 1];
    const coreColumnDefs = columns.slice(0, -1);
    const allColumnDefs = [...coreColumnDefs, ...extraColumns, actionsColumnDef];
    const extraColumnKeys = extraColumns.map((col) => col.key);

    const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);

    const {
        visibleKeys: visibleColumnKeys,
        saveVisibleKeys: saveVisibleColumnKeys,
        filteredColumns,
        saving: savingColumnPreference,
    } = useColumnVisibility(
        "billReimbursementApproval",
        allColumnDefs,
        extraColumnKeys,
        ["employeeCode", "employeeName", "actions"]
    );

    // antd's popup doesn't reliably reposition on scroll here - force it closed.
    useEffect(() => {
        const handleScroll = (event) => {
            if (event?.target?.closest?.('.column-selector-menu')) return;
            setColumnsMenuOpen(false);
        };
        window.addEventListener("scroll", handleScroll, true);
        return () => {
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, []);

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => {
            setSelectedRowKeys(selectedKeys);
        },
        getCheckboxProps: (record) => {
            if (record?.canAct === false) return { disabled: true };
            // statusbyRM is the RM stage's own field - it flips away from
            // 'Pending' as soon as the chain advances past RM, even while
            // it's genuinely ARM's turn next. getRMClaims already filters
            // the list down to rows that are this viewer's actual turn, so
            // for an engine-managed row trust that instead of the RM-specific
            // legacy field.
            // Admin's list here is the unfiltered, company-wide oversight view
            // (unlike RM's, which getRMClaims already narrows to "genuinely my
            // turn") - a row can be Pending while sitting at a level Admin
            // isn't the approver for yet (e.g. still awaiting RM). Trust
            // isPendingForViewer over the flat legacy fields for both roles
            // when it's present.
            if (record?.approval_progress) {
                // An override holder is never the current stage's own
                // approver - that's the whole point of an override - so
                // isPendingForViewer alone would lock out exactly the person
                // an out-of-chain/final-stage grant was switched on for.
                return {
                    disabled:
                        record.approval_progress.isPendingForViewer === false &&
                        record.approval_progress.canOverrideForViewer !== true,
                };
            }

            let isDisabled = false;

            if (isRM || isSubAdmin) {
                // For RM, disable if RM status is not Pending
                isDisabled = record?.statusbyRM !== 'Pending';
            } else if (isAdmin) {
                // For Admin, disable if:
                // 1. Final status is not Pending, OR
                // 2. RM hasn't approved it yet (statusbyRM is not 'Approved')
                isDisabled = record?.statusFinal !== 'Pending';
            }

            return {
                disabled: isDisabled,
            };
        },
    };

    useEffect(() => {
        fetchEmployees();
        // Set initial status filter based on role
        // if (isRM) {
        //     setStatusFilter('Pending');
        // } else if (isAdmin) {
        //     setStatusFilter('allPending');
        // }
    }, []);

    useEffect(() => {
        fetchClaimRecords();
    }, [statusFilter, selectedEmployee, applicationDate, reportingManagerFilter, pagination.current, pagination.pageSize]);

    // Reset export columns when opening modal if none selected
    useEffect(() => {
        if (isExportModalOpen && selectedExportColumns.length === 0 && availableExportColumns.length > 0) {
            setSelectedExportColumns(availableExportColumns.map((col) => col.key));
        }
    }, [isExportModalOpen]);

    const exportPayload = () => {
        const payload = {
            selected_claims: selectedRowKeys,
            selected_columns: selectedExportColumns,
            employee_id: selectedEmployee || undefined,
            applicationDate: applicationDate ? applicationDate.format('YYYY-MM-DD') : undefined,
        };
        if (isAdmin && statusFilter) payload.filter = statusFilter;
        if (isRM && statusFilter) payload.statusbyRM = statusFilter;
        return payload;
    };

    const handleExportDetailedCSV = async () => {
        try {
            dispatch(showAuthLoader());
            const reqBody = exportPayload();
            delete reqBody.selected_columns; // Detailed export has fixed columns
            const response = await Service.makeAPICall({
                methodName: Service.postMethod,
                api_url: Service.exportClaimDetailedCSV,
                body: reqBody,
            });
            dispatch(hideAuthLoader());
            if (response?.data?.data && response?.data?.status === 200) {
                const base64 = response.data.data;
                const linkSource = 'data:text/csv;base64,' + base64;
                const downloadLink = document.createElement('a');
                downloadLink.href = linkSource;
                downloadLink.download = 'ClaimApprovalRecords_Detailed.csv';
                downloadLink.style.display = 'none';
                downloadLink.click();
                downloadLink.remove();
                message.success(response?.data?.message || response?.message || 'Export successful');
            } else {
                message.error(response?.data?.message || response?.message || 'Export failed');
            }
        } catch (error) {
            dispatch(hideAuthLoader());
            console.error(error);
            message.error('Export failed');
        }
    };

    const handleExportCSV = async () => {
        try {
            dispatch(showAuthLoader());
            const reqBody = exportPayload();
            const response = await Service.makeAPICall({
                methodName: Service.postMethod,
                api_url: Service.exportClaimCSV,
                body: reqBody,
            });
            dispatch(hideAuthLoader());
            if (response?.data?.data && response?.data?.status === 200) {
                const base64 = response.data.data;
                const linkSource = 'data:text/csv;base64,' + base64;
                const downloadLink = document.createElement('a');
                downloadLink.href = linkSource;
                downloadLink.download = 'ClaimApprovalRecords.csv';
                downloadLink.style.display = 'none';
                downloadLink.click();
                downloadLink.remove();
                message.success(response?.data?.message || response?.message || 'Export successful');
            } else {
                message.error(response?.data?.message || response?.message || 'Export failed');
            }
        } catch (error) {
            dispatch(hideAuthLoader());
            console.error(error);
            message.error('Export failed');
        }
    };

    const handleExportModalOk = async () => {
        if (selectedExportColumns.length === 0) {
            message.error('Please select at least one column');
            return;
        }
        setIsExportModalOpen(false);
        await handleExportCSV();
    };

    const openFilterModal = () => {
        // Set form values before opening modal
        filterForm.setFieldsValue({
            employee: selectedEmployee,
            applicationDate: applicationDate,
            status: statusFilter,
            filter_reporting_manager: reportingManagerFilter || 'Select',
        });
        setIsFilterModalOpen(true);
    };

    const closeFilterModal = () => {
        setIsFilterModalOpen(false);
    };

    const handleFilterApply = (values) => {
        // Apply filters from modal form
        setSelectedEmployee(values.employee || null);
        setApplicationDate(values.applicationDate || null);
        setStatusFilter(values.status || '');
        setReportingManagerFilter(
            values.filter_reporting_manager && values.filter_reporting_manager !== 'Select'
                ? values.filter_reporting_manager
                : null
        );

        // Reset to first page when applying filters
        setPagination({ ...pagination, current: 1 });

        // Close modal
        setIsFilterModalOpen(false);
    };

    const handleResetInModal = () => {
        filterForm.resetFields();
    };

    // Check if any filter is applied
    const hasAppliedFilter = selectedEmployee || applicationDate || statusFilter || reportingManagerFilter;

    const canBulkAction = getResource(config.RESOURCES.BILLREIMBURSEMENT_APPROVAL, ["update"]);

    const tourSteps = useMemo(() => {
        const steps = [
            {
                id: "cam-filter",
                target: '[data-tour="cam-filter"]',
                title: "Filter & Export",
                content: "Filter claims by employee, date, status, or reporting manager. Export downloads the current list, or choose which columns to show.",
            },
        ];
        if (canBulkAction) {
            steps.push({
                id: "cam-bulk-actions",
                target: '[data-tour="cam-bulk-actions"]',
                title: "Bulk Approve / Reject",
                content: "Select rows with the checkboxes, then Approve or Reject them all at once.",
            });
        }
        steps.push({
            id: "cam-table",
            target: '[data-tour="cam-table"]',
            title: "Claims List",
            content: "Click any row to view that claim's full details.",
        });
        return steps;
    }, [canBulkAction]);

    const hintSteps = useMemo(() => [
        {
            target: '[data-tour="cam-guide-icon"]',
            title: "New here?",
            content: "Click here for a quick guide to this screen.",
            shape: "circle",
        },
    ], []);

    return (
        <Card className="employee-card">
            <div className=" inout-header text-left">
                <h2>
                    Claim Approval Records{" "}
                    <QuestionCircleOutlined
                        data-tour="cam-guide-icon"
                        className="guide-title-icon"
                        role="button"
                        tabIndex={0}
                        onClick={() => setIsTourOpen(true)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") setIsTourOpen(true);
                        }}
                        title="Click for a step-by-step guide"
                    />
                </h2>
            </div>
            <Card className="main-content-wrapper">
            <div className="search-wrapper" data-tour="cam-filter">


                <div className="filter-btns-wrapper">
                <Space>
                    <Button type="primary" className="filter-btn" onClick={openFilterModal}>
                        Filter
                    </Button>
                    <Button
                        onClick={handleClearFilters}
                        disabled={!hasAppliedFilter}
                        className="ant-delete"
                    >
                        Reset
                    </Button>
                    <Dropdown
                        overlay={
                            <Menu>
                                <Menu.Item
                                    key="exportCsv"
                                    disabled={claimData.length === 0}
                                    onClick={() => setIsExportModalOpen(true)}
                                >
                                    Export CSV
                                </Menu.Item>
                                <Menu.Item
                                    key="exportDetailedCsv"
                                    disabled={claimData.length === 0}
                                    onClick={handleExportDetailedCSV}
                                >
                                    Export Detailed CSV
                                </Menu.Item>
                            </Menu>
                        }
                    >
                        <Button className="btn-secondary">
                            Export CSV <IoIosArrowDown />
                        </Button>
                    </Dropdown>
                    <ColumnSelector
                        columns={allColumnDefs}
                        visibleKeys={visibleColumnKeys}
                        onSave={saveVisibleColumnKeys}
                        alwaysVisibleKeys={["employeeCode", "employeeName", "actions"]}
                        open={columnsMenuOpen}
                        onOpenChange={setColumnsMenuOpen}
                        saving={savingColumnPreference}
                    />
                    <Space data-tour="cam-bulk-actions">
                        {(getResource(config.RESOURCES.BILLREIMBURSEMENT_APPROVAL, ["update"]) )
                        ? (
                            <>
                        <Button
                            type="primary"
                            disabled={selectedRowKeys.length === 0}
                            onClick={handleBulkApprove}
                        >
                            Approve
                        </Button>
                        <Button
                            danger
                            disabled={selectedRowKeys.length === 0}
                            onClick={handleBulkReject}
                        >
                            Reject
                    </Button>
                        </>
                    ) : ""}

                </Space>
                </Space>
                </div>
            </div>
            <div className="main-table-wrapper" data-tour="cam-table">
            {/* Claims Table */}
            <Table
                rowSelection={rowSelection}
                columns={filteredColumns}
                scroll={{ x: "max-content" }}
                dataSource={claimData}
                rowKey="_id"
                onRow={(record) => {
                    return {
                      onClick: (event) => {
                        const isActionColumn = event.target.closest('.ant-table-cell')?.classList.contains('actions-column');
                        
                        if (!isActionColumn) {
                          history.push(`/${companySlug}/bill-reimbursement/view/${record._id}`);
                        }
                      },
                      style: { cursor: 'pointer' },
                    };
                  }}
                pagination={{
                    ...TABLE_PAGINATION,
                    ...pagination,
                    total: totalRecords,
                    onChange: (page, pageSize) => {
                        setPagination((prev) => ({
                            current: pageSize !== prev.pageSize ? 1 : page,
                            pageSize,
                        }));
                    },
                }}
                footer={() => <div>Total Records: {totalRecords}</div>}
            />
            </div>
            </Card>
            {/* Filter Modal */}
            <Modal
                title="Filter Claims"
                width={600}
                open={isFilterModalOpen}
                onCancel={closeFilterModal}
                footer={[
                    <Button key="cancel" className="ant-delete" onClick={closeFilterModal}>
                        Cancel
                    </Button>,
                    <Button key="reset" className="ant-delete" onClick={handleResetInModal}>
                        Reset
                    </Button>,
                    <Button key="apply" type="primary" onClick={() => filterForm.submit()}>
                        Apply
                    </Button>,
                ]}
                style={{ top: "40px" }}
            >
                <Form form={filterForm} onFinish={handleFilterApply} layout="vertical">
                    <Form.Item label="Employee Code/Name" name="employee">
                        <Select
                            showSearch
                            allowClear
                            placeholder="Select Employee"
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                String(option?.children ?? '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {employees.map(emp => (
                                <Option key={emp._id} value={emp._id}>
                                    {`${emp.emp_code}: ${emp.full_name}`}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="Date" name="applicationDate">
                        <DatePicker
                            style={{ width: '100%' }}
                            format="DD-MM-YYYY"
                        />
                    </Form.Item>

                    {isAdminRole && (
                        <Form.Item
                            label="Reporting Manager"
                            name="filter_reporting_manager"
                            initialValue="Select"
                        >
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                options={reportingManagerOptions}
                            />
                        </Form.Item>
                    )}

                    {isRM && (
                        <Form.Item label="Status" name="status">
                            <Radio.Group>
                                <Radio value="Pending">Pending</Radio>
                                <Radio value="Approved">Approved</Radio>
                                <Radio value="Rejected">Rejected</Radio>
                            </Radio.Group>
                        </Form.Item>
                    )}

                    {isAdmin && (
                        <Form.Item label="Status" name="status">
                            <Radio.Group>
                                <Radio value="allPending">All Pending</Radio>
                                <Radio value="adminPending">Admin Pending</Radio>
                                <Radio value="rmApproved">RM Approved</Radio>
                                <Radio value="completed">Completed</Radio>
                            </Radio.Group>
                        </Form.Item>
                    )}
                </Form>
            </Modal>
            {/* Export Modal */}
            <Modal
                title="Select Columns for CSV Export"
                open={isExportModalOpen}
                onCancel={() => setIsExportModalOpen(false)}
                onOk={handleExportModalOk}
                okText="Export CSV"
                cancelText="Cancel"
                width={700}
            >
                <div style={{ marginBottom: 12, padding: '10px', background: '#f0f7ff', borderRadius: 4, borderLeft: '4px solid #187cb7', fontSize: 12 }}>
                    <strong>Note:</strong> Claims are exported based on the currently applied filter. With no status filter, all claims (including <strong>Pending</strong>) are exported.
                </div>
                <div style={{ marginBottom: 16 }}>
                    <Checkbox
                        checked={
                            availableExportColumns.length > 0 &&
                            selectedExportColumns.length === availableExportColumns.length
                        }
                        indeterminate={
                            selectedExportColumns.length > 0 &&
                            selectedExportColumns.length < availableExportColumns.length
                        }
                        onChange={(e) => {
                            if (e.target.checked) {
                                setSelectedExportColumns(availableExportColumns.map((col) => col.key));
                            } else {
                                setSelectedExportColumns([]);
                            }
                        }}
                    >
                        Select All
                    </Checkbox>
                </div>
                <Checkbox.Group
                    value={selectedExportColumns}
                    onChange={(values) => setSelectedExportColumns(values)}
                    style={{ width: '100%' }}
                >
                    <Row gutter={[16, 8]}>
                        {availableExportColumns.map((column) => (
                            <Col xs={12} sm={8} md={6} key={column.key}>
                                <Checkbox value={column.key}>{column.label}</Checkbox>
                            </Col>
                        ))}
                    </Row>
                </Checkbox.Group>
            </Modal>
            {isTourOpen && (
                <GuidedTour steps={tourSteps} onClose={() => setIsTourOpen(false)} storageKey={TOUR_STORAGE_KEY} />
            )}
            {!isTourOpen && isTourHintOpen && (
                <GuidedTour steps={hintSteps} onClose={() => setIsTourHintOpen(false)} storageKey={TOUR_HINT_STORAGE_KEY} />
            )}
        </Card>
    );
};

export default ClaimApprovalManagement;